import { format } from 'date-fns';

import { EncounterSchema } from '../../../services/types/treatment-types';
import { Badge } from '../../ui/catalyst/badge';
import { EmptyListPlaceholder } from '../../ui/EmptyListPlaceholder';

interface EncountersMacroViewProps {
  encounters: EncounterSchema[];
  onSelectEncounter: (encounterId: string) => void;
  onAddEncounter: () => void;
}

const EncounterMacroItem = ({
  encounter,
  onSelect,
}: EncounterMacroItemProps) => {
  const hasDate = Boolean(encounter.date);
  let formattedDate = '';

  if (hasDate) {
    const date = new Date(encounter.date as string);
    formattedDate = format(date, 'MMM d, yyyy');
  }

  const handleClick = () => {
    if (encounter.id) {
      onSelect(encounter.id);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      className="flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`View encounter from ${hasDate ? formattedDate : 'undated'}`}
    >
      {/* Date section */}
      <div className="flex-shrink-0 w-32">
        {hasDate ? (
          <span className="text-sm font-medium text-gray-900">
            {formattedDate}
            {encounter.source === 'check_in' && (
              <span className="text-[10px] text-blue-500 font-medium ml-1.5">check-in</span>
            )}
          </span>
        ) : (
          <div className="flex items-center">
            <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mr-1.5" />
            <span className="text-sm font-medium text-amber-600">Undated</span>
          </div>
        )}
      </div>

      {/* Services section */}
      <div className="flex-1 min-w-0 ml-4">
        {encounter.services && encounter.services.length > 0 ? (
          <div className="flex flex-wrap gap-1 max-w-full">
            {encounter.services.slice(0, 4).map((service, index) => (
              <Badge
                key={index}
                color="zinc"
                className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 truncate max-w-32"
              >
                {service}
              </Badge>
            ))}
            {encounter.services.length > 4 && (
              <Badge
                color="zinc"
                className="text-xs px-2 py-0.5 bg-gray-200 text-gray-700 font-medium"
              >
                +{encounter.services.length - 4}
              </Badge>
            )}
          </div>
        ) : (
          <span className="text-sm text-gray-400 italic">No services</span>
        )}
      </div>
    </div>
  );
};

export const EncountersMacroView = ({
  encounters,
  onSelectEncounter,
  onAddEncounter,
}: EncountersMacroViewProps) => {
  if (encounters.length === 0) {
    return (
      <EmptyListPlaceholder
        message="No encounters recorded"
        buttonText="Add First Encounter"
        onButtonClick={onAddEncounter}
      />
    );
  }

  // Sort encounters by date (newest first)
  const sortedEncounters = [...encounters].sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1; // Undated items go last
    if (!b.date) return -1;
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  return (
    <div className="bg-white">
      {sortedEncounters.map((encounter) => (
        <EncounterMacroItem
          key={encounter.id}
          encounter={encounter}
          onSelect={onSelectEncounter}
        />
      ))}
    </div>
  );
};

interface EncounterMacroItemProps {
  encounter: EncounterSchema;
  onSelect: (encounterId: string) => void;
}
